import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { Box, Typography, Paper, CircularProgress, Alert, useTheme, useMediaQuery } from '@mui/material';
import { useObras } from '../context/ObrasContext';
import { useAuth } from '../context/AuthContext';
import { usePermisos } from '../hooks/usePermisos';
import { presupuestoService } from '../services/presupuestoService';
import { ItemPresupuesto, Rubro } from '../types';
import { DropResult } from '@hello-pangea/dnd';
import { PresupuestoTable } from '../components/presupuesto/PresupuestoTable';

const PresupuestoPage = () => {
    const { obras, obraSeleccionada, loading: obrasLoading } = useObras();
    const { usuario } = useAuth();
    const { puedeEditar } = usePermisos(usuario);
    const isReadOnly = !puedeEditar();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [items, setItems] = useState<ItemPresupuesto[]>([]);
    const [rubros, setRubros] = useState<Rubro[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const selectedObra = obras.find(o => o.id === obraSeleccionada);

    useEffect(() => {
        if (obraSeleccionada) {
            fetchData();
        } else {
            setItems([]);
            setRubros([]);
        }
    }, [obraSeleccionada]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [itemsData, rubrosData] = await Promise.all([
                presupuestoService.getItems(obraSeleccionada),
                presupuestoService.getRubros(obraSeleccionada)
            ]);
            setItems(itemsData);
            setRubros(rubrosData);
        } catch (err) {
            console.error(err);
            setError('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleAddItem = async (newItem: Omit<ItemPresupuesto, 'id'>) => {
        try {
            const id = await presupuestoService.addItem(obraSeleccionada, newItem);
            setItems([...items, { ...newItem, id }]);
        } catch (err) {
            console.error(err);
            setError("Error al agregar item");
        }
    };

    const handleUpdateItem = async (id: string, updates: Partial<ItemPresupuesto>) => {
        try {
            await presupuestoService.updateItem(obraSeleccionada, id, updates);
            setItems(items.map(i => i.id === id ? { ...i, ...updates } : i));
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteItem = async (id: string) => {
        try {
            await presupuestoService.deleteItem(obraSeleccionada, id);
            setItems(prev => prev.filter(i => i.id !== id));
        } catch (err) {
            console.error(err);
            setError("Error al eliminar item");
        }
    }

    const handleAddRubro = async (rubro: Omit<Rubro, 'id'>): Promise<string> => {
        try {
            const id = await presupuestoService.addRubro(obraSeleccionada, rubro);
            setRubros(prev => [...prev, { ...rubro, id }]);
            return id;
        } catch (err) {
            console.error(err);
            setError("Error al agregar rubro");
            throw err;
        }
    };

    const handleDeleteRubro = async (id: string) => {
        try {
            await presupuestoService.deleteRubro(obraSeleccionada, id);
            setRubros(prev => prev.filter(r => r.id !== id));
            setItems(prev => prev.map(i => i.rubroId === id ? { ...i, rubroId: undefined } : i));
        } catch (err) {
            console.error(err);
            setError("Error al eliminar rubro");
        }
    };

    const handleBulkDelete = async (itemIds: string[], rubroIds: string[]) => {
        if (!obraSeleccionada) return;
        try {
            await presupuestoService.bulkDelete(obraSeleccionada, itemIds, rubroIds);
            
            const itemIdsSet = new Set(itemIds);
            const rubroIdsSet = new Set(rubroIds);
            
            setItems(prev => prev
                .filter(i => !itemIdsSet.has(i.id))
                .map(i => rubroIdsSet.has(i.rubroId || '') ? { ...i, rubroId: undefined } : i)
            );
            
            setRubros(prev => prev.filter(r => !rubroIdsSet.has(r.id)));
        } catch (err) {
            console.error(err);
            setError("Error en la eliminación masiva");
        }
    };

    const handleUpdateRubro = async (id: string, updates: Partial<Rubro>) => {
        try {
            await presupuestoService.updateRubro(obraSeleccionada, id, updates);
            setRubros(rubros.map(r => r.id === id ? { ...r, ...updates } : r));
        } catch (err) {
            console.error(err);
            setError("Error al actualizar rubro");
        }
    };

    const handleDragEnd = async (result: DropResult) => {
        const { destination, source, draggableId, type } = result;

        if (!destination) return;

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        if (type === 'RUBRO') {
            const newRubros = Array.from(rubros);
            const [reorderedItem] = newRubros.splice(source.index, 1);
            newRubros.splice(destination.index, 0, reorderedItem);

            const updatedRubros = newRubros.map((r, index) => ({ ...r, order: index }));
            setRubros(updatedRubros);

            try {
                await Promise.all(
                    updatedRubros.map(r => 
                        presupuestoService.updateRubro(obraSeleccionada, r.id, { order: r.order })
                    )
                );
            } catch (err) {
                console.error("Failed to reorder rubros", err);
                setError("Error al reordenar rubros");
                fetchData();
            }
            return;
        }

        const newRubroId = destination.droppableId === 'uncategorized' ? undefined : destination.droppableId;

        const updatedItems = items.map(item =>
            item.id === draggableId
                ? { ...item, rubroId: newRubroId }
                : item
        );
        setItems(updatedItems);

        try {
            await presupuestoService.updateItem(obraSeleccionada, draggableId, { rubroId: newRubroId || '' });
        } catch (err) {
            console.error("Failed to move item", err);
            setError("Error al mover item");
            fetchData();
        }
    };

    if (obrasLoading) return <CircularProgress />;

    return (
        <Layout>
            <Box sx={{ 
                mb: 4, 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: 2,
                p: isMobile ? 2 : 0
            }}>
                <Box>
                    <Typography variant="h4" sx={{ 
                        fontWeight: 900, 
                        background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5, 
                        fontSize: isMobile ? '2.2rem' : '2.8rem',
                        letterSpacing: '-0.03em',
                        textShadow: '0 2px 10px rgba(26, 35, 126, 0.1)'
                    }}>
                        Presupuesto
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 500,
                        fontSize: '1.1rem',
                        opacity: 0.8
                    }}>
                        Gestión inteligente de costos y rentabilidad
                    </Typography>
                </Box>
                {obraSeleccionada && (
                    <Box sx={{ 
                        px: 2.5, 
                        py: 1.2, 
                        background: 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '20px', 
                        border: '1px solid', 
                        borderColor: 'primary.100',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)'
                    }}>
                        <Box sx={{ 
                            width: 10, 
                            height: 10, 
                            borderRadius: '50%', 
                            bgcolor: 'primary.main',
                            boxShadow: '0 0 10px rgba(63, 81, 181, 0.5)'
                        }} />
                        <Typography variant="subtitle1" sx={{ color: 'primary.dark', fontWeight: 800 }}>
                            {selectedObra?.nombre}
                        </Typography>
                    </Box>
                )}
            </Box>


            {!obraSeleccionada ? (
                <Alert severity="info">Seleccione una obra para comenzar</Alert>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: { xs: 1, md: 4 }, 
                            borderRadius: { xs: 4, md: 6 }, 
                            background: 'rgba(255, 255, 255, 0.4)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.6)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.04)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {loading ? (
                            <CircularProgress />
                        ) : (
                            <PresupuestoTable
                                items={items}
                                rubros={rubros}
                                onUpdate={handleUpdateItem}
                                onAdd={handleAddItem}
                                onDelete={handleDeleteItem}
                                onAddRubro={handleAddRubro}
                                onDeleteRubro={handleDeleteRubro}
                                onBulkDelete={handleBulkDelete}
                                onUpdateRubro={handleUpdateRubro}
                                onDragEnd={handleDragEnd}
                                obra={selectedObra}
                                readOnly={isReadOnly}
                            />
                        )}
                    </Paper>
                </Box>
            )}
        </Layout>
    );
};

export default PresupuestoPage;
